import { IndependenceConfirmationPopup } from '@shared/ui';
import { useAudits, useConfirmIndependence, useRejectIndependence } from '@/api';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type RequestParams = {
  auditId: number;
  isAuditor: boolean;
  independenceStatus: boolean | null | undefined;
  isArchived?: boolean;
};

type IndependenceContextValue = {
  requestConfirmation: (params: RequestParams) => Promise<void>;
};

type PendingRequest = {
  auditId: number;
  resolve: () => void;
  reject: (error?: unknown) => void;
};

const IndependenceConfirmationContext = createContext<IndependenceContextValue>({
  requestConfirmation: () => Promise.resolve(),
});

export const useIndependenceConfirmation = () => useContext(IndependenceConfirmationContext);

/** Where the dashboard's independence question stands for one audit. */
export type IndependenceGate = 'asking' | 'clear' | 'declined';

/**
 * A DEEP LINK ASKS TOO. The API answers 403 on every audit route to an auditor seated on the audit
 * who has not confirmed independence (`middleware/attachAuditById.js`), and a 403 ends the session.
 * The audit list and the audit switcher ask before they open an audit; a pasted link, a bookmark or
 * a notification lands on the dashboard directly, so the dashboard asks with this before its first
 * read of the audit. `GET /audit`, outside the audit routes, says whether the caller holds a seat
 * and has confirmed. An audit it does not list (no seat, or the list failed) is left to the audit
 * read, as before.
 */
export const useIndependenceGate = (auditId: string): IndependenceGate => {
  const { requestConfirmation } = useIndependenceConfirmation();
  const { data, isPending } = useAudits();
  const [answer, setAnswer] = useState<{ auditId: string; gate: IndependenceGate } | null>(null);

  const audit = useMemo(
    () =>
      data?.companies
        .flatMap((company) => company.audits)
        .find((entry) => String(entry.id) === auditId),
    [auditId, data],
  );
  const isAuditor = Boolean(audit?.isAuditor);
  const independenceStatus =
    audit?.myIndependenceStatus ?? audit?.isAuditor?.independenceConfirmed ?? null;
  const isArchived = audit?.archived;

  useEffect(() => {
    if (isPending) return undefined;
    let current = true;
    requestConfirmation({
      auditId: Number(auditId),
      isAuditor,
      independenceStatus,
      isArchived,
    }).then(
      () => {
        if (current) setAnswer({ auditId, gate: 'clear' });
      },
      () => {
        if (current) setAnswer({ auditId, gate: 'declined' });
      },
    );
    return () => {
      current = false;
    };
  }, [auditId, isPending, isAuditor, independenceStatus, isArchived, requestConfirmation]);

  return answer?.auditId === auditId ? answer.gate : 'asking';
};

export const IndependenceConfirmationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const { mutateAsync: confirmIndependence, isPending: isConfirming } = useConfirmIndependence();
  const { mutateAsync: rejectIndependence, isPending: isRejecting } = useRejectIndependence();
  const confirmedAuditsRef = useRef<Set<number>>(new Set());

  const requestConfirmation = useCallback(
    ({ auditId, isAuditor, independenceStatus, isArchived }: RequestParams) => {
      if (
        !isAuditor ||
        independenceStatus === true ||
        confirmedAuditsRef.current.has(auditId) ||
        isArchived
      ) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        setPendingRequest({ auditId, resolve, reject });
      });
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (!pendingRequest) return;

    try {
      await confirmIndependence({ auditId: pendingRequest.auditId });
      confirmedAuditsRef.current.add(pendingRequest.auditId);
      pendingRequest.resolve();
    } catch (error) {
      console.error(error);
      pendingRequest.reject(error);
    } finally {
      setPendingRequest(null);
    }
  }, [confirmIndependence, pendingRequest]);

  const handleReject = useCallback(async () => {
    if (!pendingRequest) return;

    try {
      await rejectIndependence({ auditId: pendingRequest.auditId });
      pendingRequest.reject(new Error('independence_rejected'));
    } catch (error) {
      console.error(error);
      // Settle the pending promise even when the backend call fails,
      // otherwise the caller's navigation handler hangs forever.
      pendingRequest.reject(error);
    } finally {
      setPendingRequest(null);
    }
  }, [pendingRequest, rejectIndependence]);

  // Closing the question settles it as declined, so a caller waiting on it (the dashboard's gate)
  // is never left waiting; the lists do nothing on either.
  const handleClose = useCallback(() => {
    pendingRequest?.reject(new Error('independence_dismissed'));
    setPendingRequest(null);
  }, [pendingRequest]);

  const value = useMemo(
    () => ({
      requestConfirmation,
    }),
    [requestConfirmation],
  );

  return (
    <IndependenceConfirmationContext.Provider value={value}>
      {children}
      <IndependenceConfirmationPopup
        open={Boolean(pendingRequest)}
        confirmLoading={isConfirming}
        conflictLoading={isRejecting}
        onClose={handleClose}
        onConfirm={handleConfirm}
        onConflict={handleReject}
      />
    </IndependenceConfirmationContext.Provider>
  );
};
